import { cn } from "@/lib/utils";

/**
 * Minimal inline-SVG national flags. Keyed by lowercase country name or
 * ISO-ish slug. Falls back to a neutral gray chip with the first 3 letters.
 */
const FLAGS: Record<string, () => React.JSX.Element> = {
  argentina: () => (
    <g>
      <rect width="48" height="32" fill="#75aadb" />
      <rect y="10.66" width="48" height="10.66" fill="#fff" />
      <circle cx="24" cy="16" r="3" fill="#f6b40e" stroke="#85340a" strokeWidth="0.4" />
    </g>
  ),
  brazil: () => (
    <g>
      <rect width="48" height="32" fill="#009c3b" />
      <polygon points="24,4 44,16 24,28 4,16" fill="#ffdf00" />
      <circle cx="24" cy="16" r="5.5" fill="#002776" />
    </g>
  ),
  france: () => (
    <g>
      <rect width="16" height="32" fill="#002395" />
      <rect x="16" width="16" height="32" fill="#fff" />
      <rect x="32" width="16" height="32" fill="#ed2939" />
    </g>
  ),
  spain: () => (
    <g>
      <rect width="48" height="32" fill="#c60b1e" />
      <rect y="8" width="48" height="16" fill="#ffc400" />
    </g>
  ),
  italy: () => (
    <g>
      <rect width="16" height="32" fill="#009246" />
      <rect x="16" width="16" height="32" fill="#fff" />
      <rect x="32" width="16" height="32" fill="#ce2b37" />
    </g>
  ),
  england: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect x="20" width="8" height="32" fill="#ce1124" />
      <rect y="12" width="48" height="8" fill="#ce1124" />
    </g>
  ),
  germany: () => (
    <g>
      <rect width="48" height="32" fill="#000" />
      <rect y="10.66" width="48" height="10.66" fill="#dd0000" />
      <rect y="21.32" width="48" height="10.68" fill="#ffce00" />
    </g>
  ),
  portugal: () => (
    <g>
      <rect width="48" height="32" fill="#da291c" />
      <rect width="19" height="32" fill="#046a38" />
      <circle cx="19" cy="16" r="4.5" fill="#ffce00" stroke="#fff" strokeWidth="0.5" />
    </g>
  ),
  netherlands: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect width="48" height="10.66" fill="#ae1c28" />
      <rect y="21.32" width="48" height="10.68" fill="#21468b" />
    </g>
  ),
  croatia: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect width="48" height="10.66" fill="#ff0000" />
      <rect y="21.32" width="48" height="10.68" fill="#171796" />
    </g>
  ),
  uruguay: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      {[0, 2, 4, 6].map((i) => (
        <rect key={i} y={4 + i * 4} width="48" height="2" fill="#0038a8" />
      ))}
      <rect width="20" height="18" fill="#fff" />
      <circle cx="10" cy="9" r="4" fill="#fcd116" />
    </g>
  ),
  chile: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect y="16" width="48" height="16" fill="#d52b1e" />
      <rect width="20" height="16" fill="#0039a6" />
      <polygon points="10,4 11.5,8.5 16,8.5 12.3,11.2 13.8,15.5 10,12.8 6.2,15.5 7.7,11.2 4,8.5 8.5,8.5" fill="#fff" />
    </g>
  ),
  colombia: () => (
    <g>
      <rect width="48" height="16" fill="#fcd116" />
      <rect y="16" width="48" height="8" fill="#003893" />
      <rect y="24" width="48" height="8" fill="#ce1126" />
    </g>
  ),
  mexico: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect width="16" height="32" fill="#006847" />
      <rect x="32" width="16" height="32" fill="#ce1126" />
    </g>
  ),
  usa: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      {[0, 2, 4, 6, 8, 10, 12].map((i) => (
        <rect key={i} y={i * 2.46} width="48" height="2.46" fill={i % 2 === 0 ? "#b22234" : "#fff"} />
      ))}
      <rect width="20" height="17" fill="#3c3b6e" />
    </g>
  ),
  morocco: () => (
    <g>
      <rect width="48" height="32" fill="#c1272d" />
      <polygon points="24,11 26,17 32,17 27,20 29,26 24,22 19,26 21,20 16,17 22,17" fill="none" stroke="#006233" strokeWidth="1" />
    </g>
  ),
  saudi: () => (
    <g>
      <rect width="48" height="32" fill="#006c35" />
      <rect x="6" y="20" width="36" height="2" fill="#fff" />
    </g>
  ),
  australia: () => (
    <g>
      <rect width="48" height="32" fill="#012169" />
      <rect width="22" height="16" fill="#012169" />
      <path d="M0 0 L22 16 M22 0 L0 16" stroke="#fff" strokeWidth="2" />
      <path d="M11 0 V16 M0 8 H22" stroke="#fff" strokeWidth="3" />
      <path d="M11 0 V16 M0 8 H22" stroke="#c8102e" strokeWidth="1.5" />
    </g>
  ),
  paraguay: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect width="48" height="10.66" fill="#d52b1e" />
      <rect y="21.32" width="48" height="10.68" fill="#0038a8" />
    </g>
  ),
  peru: () => (
    <g>
      <rect width="48" height="32" fill="#d91023" />
      <rect x="16" width="16" height="32" fill="#fff" />
    </g>
  ),
  ecuador: () => (
    <g>
      <rect width="48" height="32" fill="#ffd100" />
      <rect y="16" width="48" height="8" fill="#0072ce" />
      <rect y="24" width="48" height="8" fill="#ef3340" />
    </g>
  ),
  venezuela: () => (
    <g>
      <rect width="48" height="32" fill="#ffd100" />
      <rect y="10.66" width="48" height="10.66" fill="#00247d" />
      <rect y="21.32" width="48" height="10.68" fill="#cf142b" />
    </g>
  ),
  belgium: () => (
    <g>
      <rect width="16" height="32" fill="#000" />
      <rect x="16" width="16" height="32" fill="#fae042" />
      <rect x="32" width="16" height="32" fill="#ed2939" />
    </g>
  ),
  poland: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect y="16" width="48" height="16" fill="#dc143c" />
    </g>
  ),
  switzerland: () => (
    <g>
      <rect width="48" height="32" fill="#d52b1e" />
      <rect x="20" y="6" width="8" height="20" fill="#fff" />
      <rect x="14" y="12" width="20" height="8" fill="#fff" />
    </g>
  ),
  japan: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <circle cx="24" cy="16" r="9" fill="#bc002d" />
    </g>
  ),
  korea: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <circle cx="24" cy="16" r="7" fill="#cd2e3a" />
      <path d="M17 16 A7 7 0 0 1 31 16 A3.5 3.5 0 0 1 24 16 A3.5 3.5 0 0 0 17 16" fill="#0047a0" />
    </g>
  ),
  egypt: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect width="48" height="10.66" fill="#ce1126" />
      <rect y="21.32" width="48" height="10.68" fill="#000" />
      <circle cx="24" cy="16" r="2.6" fill="#c09300" stroke="#85340a" strokeWidth="0.3" />
    </g>
  ),
  jordan: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect width="48" height="10.66" fill="#000" />
      <rect y="21.32" width="48" height="10.68" fill="#007a3d" />
      <polygon points="0,0 20,16 0,32" fill="#ce1126" />
      <polygon points="6,14 7,16.6 9.6,16.6 7.5,18.2 8.3,20.8 6,19.2 3.7,20.8 4.5,18.2 2.4,16.6 5,16.6" fill="#fff" />
    </g>
  ),
  algeria: () => (
    <g>
      <rect width="24" height="32" fill="#006233" />
      <rect x="24" width="24" height="32" fill="#fff" />
      <circle cx="26" cy="16" r="6" fill="#d21034" />
      <circle cx="27.5" cy="16" r="5" fill="#fff" />
      <polygon
        points="30,16 32.5,17 31,14.6 33.5,13 30.5,13 30,10.5 29.5,13 26.5,13 29,14.6 27.5,17"
        fill="#d21034"
      />
    </g>
  ),
  austria: () => (
    <g>
      <rect width="48" height="32" fill="#fff" />
      <rect width="48" height="10.66" fill="#ed2939" />
      <rect y="21.32" width="48" height="10.68" fill="#ed2939" />
    </g>
  ),
};

function slug(name: string) {
  const k = name.toLowerCase().trim();
  const map: Record<string, string> = {
    "south korea": "korea",
    "republic of korea": "korea",
    "united states": "usa",
    "united kingdom": "england",
    "great britain": "england",
    "saudi arabia": "saudi",
    "the netherlands": "netherlands",
    holland: "netherlands",
  };
  return map[k] ?? k.replace(/\s+/g, "");
}

export function Flag({
  country,
  className,
  rounded = true,
}: {
  country: string;
  className?: string;
  rounded?: boolean;
}) {
  const key = slug(country);
  const draw = FLAGS[key];
  if (!draw) {
    return (
      <span
        className={cn(
          "inline-flex h-5 w-7 items-center justify-center bg-muted text-[0.55rem] font-bold uppercase text-muted-foreground",
          rounded && "rounded-sm",
          className,
        )}
        aria-label={country}
        title={country}
      >
        {country.slice(0, 3)}
      </span>
    );
  }
  return (
    <svg
      viewBox="0 0 48 32"
      className={cn("inline-block h-5 w-7 shrink-0 ring-1 ring-black/10", rounded && "rounded-sm", className)}
      aria-label={country}
      role="img"
    >
      {draw()}
    </svg>
  );
}

const INTERNATIONAL_OPPONENTS = [
  "Egypt",
  "Jordan",
  "Algeria",
  "Austria",
  "Brazil",
  "Uruguay",
  "Chile",
  "Colombia",
  "Mexico",
  "France",
  "Spain",
  "Italy",
  "England",
  "Germany",
  "Portugal",
  "Netherlands",
  "Croatia",
  "Morocco",
  "Saudi Arabia",
  "Australia",
  "Paraguay",
  "Peru",
  "Ecuador",
  "Venezuela",
  "Belgium",
  "Poland",
  "Switzerland",
  "Japan",
  "South Korea",
  "USA",
];

function normalizeMatchText(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function getArgentinaMatchOpponent(input: string | null | undefined) {
  if (!input) return null;
  const text = ` ${normalizeMatchText(input)} `;
  if (!text.includes(" argentina ")) return null;

  return INTERNATIONAL_OPPONENTS.find((country) => {
    const key = normalizeMatchText(country);
    return text.includes(` ${key} `) || text.includes(` ${slug(country)} `);
  }) ?? null;
}

export function MatchFlags({ opponent, className }: { opponent: string | null | undefined; className?: string }) {
  if (!opponent) return null;

  return (
    <span
      role="img"
      aria-label={`Argentina vs ${opponent}`}
      className={cn("inline-flex items-center gap-1.5", className)}
    >
      <Flag country="argentina" className="h-4 w-6" />
      <span aria-hidden className="h-px w-4 bg-border" />
      <Flag country={opponent} className="h-4 w-6" />
    </span>
  );
}
